import FormattedMessage from 'I18n/components/FormattedMessage';
import { SuccessIcon } from 'foundations-assets/icons/components/SuccessIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Link } from 'foundations-components/transitional/components/Link';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Table, TableHeaderCell } from 'foundations-components/transitional/components/Table';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { FLATTENED_TIER_ORDER } from 'marketplace-ui-common/compatibility/constants';
import * as TierGroups from 'marketplace-ui-common/constants/groupTags/TierGroups';
import { getPricingUrl } from 'marketplace-ui-common/utils/urls';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { tierGroupNamesByProductType } from './constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function CompatibilityModal({
  appContext,
  featuresWithTierCompatibility,
  onClose,
  onTrackInteraction = () => {},
  portalTiers,
  productType
}) {
  const theme = useTheme();
  const tierGroupNames = useMemo(() => tierGroupNamesByProductType[productType] || [], [productType]);
  const renderedStickyFeaturesTable = useMemo(() => {
    return /*#__PURE__*/_jsx("div", {
      style: {
        position: 'sticky',
        left: '0px',
        top: '0px',
        zIndex: 1111
      },
      children: /*#__PURE__*/_jsxs(Table, {
        hover: false,
        condensed: true,
        children: [/*#__PURE__*/_jsx("colgroup", {
          children: /*#__PURE__*/_jsx("col", {
            style: {
              minWidth: 300
            }
          })
        }), /*#__PURE__*/_jsxs("tbody", {
          children: [/*#__PURE__*/_jsx("tr", {
            children: /*#__PURE__*/_jsx("td", {})
          }), /*#__PURE__*/_jsx("tr", {
            children: /*#__PURE__*/_jsx(TableHeaderCell, {
              children: /*#__PURE__*/_jsx(Text, {
                textTransform: "uppercase",
                typography: "heading-25",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "detailsPage.compatibility.modal.features"
                })
              })
            })
          }), featuresWithTierCompatibility.map((feature, featureIndex) => /*#__PURE__*/_jsx("tr", {
            children: /*#__PURE__*/_jsx("td", {
              children: /*#__PURE__*/_jsx(TruncateString, {
                maxWidth: 300,
                children: feature.text
              })
            })
          }, `feature-row-${featureIndex}`)), /*#__PURE__*/_jsx("tr", {
            children: /*#__PURE__*/_jsx("td", {})
          })]
        })]
      })
    });
  }, [featuresWithTierCompatibility]);
  const renderedHubsHeader = /*#__PURE__*/_jsx("tr", {
    children: tierGroupNames.map(tierGroup =>
    /*#__PURE__*/
    /* @bend-expect trellis-tokenized */
    _jsx("td", {
      colSpan: TierGroups[tierGroup].length,
      style: {
        borderLeft: `1px solid ${theme.color['border-core-subtle']}`
      },
      children: /*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        justifyContent: "center",
        children: /*#__PURE__*/_jsx(Text, {
          typography: "heading-50",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `marketplace-ui-common.groupTags.tierHub.${tierGroup}`
          })
        })
      })
    }, tierGroup))
  });
  const renderedHubLevels = useMemo(() => /*#__PURE__*/_jsx("tr", {
    children: tierGroupNames.map(tierGroup => TierGroups[tierGroup].map(tier => {
      const tierLevel = tier.split('_')[1];
      const isUserPlan = portalTiers === null || portalTiers === void 0 ? void 0 : portalTiers.includes(tier);
      return /*#__PURE__*/_jsxs(TableHeaderCell, {
        textAlign: "center",
        children: [isUserPlan ? /*#__PURE__*/_jsx(View, {
          marginBlockStart: "-700",
          marginBlockEnd: "250",
          children: /*#__PURE__*/_jsx(Badge, {
            use: "default",
            children: /*#__PURE__*/_jsx(Text, {
              as: "span",
              whiteSpace: "nowrap",
              color: "fill-surface-default",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "detailsPage.compatibility.modal.yourPlan"
              })
            })
          })
        }) : null, /*#__PURE__*/_jsx(Text, {
          textTransform: "uppercase",
          typography: "heading-25",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `marketplace-ui-common.groupTags.tierLevel.${tierLevel}`
          })
        })]
      }, tier);
    }))
  }), [portalTiers, tierGroupNames]);
  const renderedFeatureRows = useMemo(() => featuresWithTierCompatibility.map((feature, featureIndex) => /*#__PURE__*/_jsx("tr", {
    children: tierGroupNames.map(tierGroup => {
      return TierGroups[tierGroup].map((tier, index) => {
        const isFeatureCompatible = feature.tiers.includes(tier);
        return (
          /*#__PURE__*/
          /* @bend-expect trellis-tokenized */
          _jsx("td", {
            style: {
              borderLeft: index === 0 ? `1px solid ${theme.color['border-core-subtle']}` : undefined
            },
            children: isFeatureCompatible ? /*#__PURE__*/_jsx(SuccessIcon, {
              "data-test-id": `${tier}-feature-${featureIndex}-check`,
              color: "fill-positive-default"
            }) : null
          }, `${tier}-feature-${featureIndex}`)
        );
      });
    })
  }, `feature-row-${featureIndex}`)), [featuresWithTierCompatibility, tierGroupNames, theme.color]);
  const renderedFooter = useMemo(() => /*#__PURE__*/_jsx("tr", {
    children: tierGroupNames.map(tierGroup =>
    /*#__PURE__*/
    /* @bend-expect trellis-tokenized */
    _jsx("td", {
      colSpan: TierGroups[tierGroup].length,
      style: {
        borderLeft: `1px solid ${theme.color['border-core-subtle']}`
      },
      children: /*#__PURE__*/_jsx(Text, {
        as: "small",
        children: /*#__PURE__*/_jsx(Link, {
          "data-test-id": `${tierGroup}-pricing-link`,
          external: true,
          href: getPricingUrl(appContext !== 'app', tierGroup, undefined, 'marketplace-compatibility'),
          onClick: () => {
            onTrackInteraction({
              action: 'click compatibility modal pricing link',
              value: tierGroup
            });
          },
          children: tierGroup === 'CRM' ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.compatibility.modal.seeFeatures"
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.compatibility.modal.seePricingAndFeatures"
          })
        })
      })
    }, tierGroup))
  }), [appContext, onTrackInteraction, tierGroupNames, theme.color]);
  return /*#__PURE__*/_jsxs(Modal, {
    width: "90%",
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h2",
        color: "fill-positive-default",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.compatibility.modal.header"
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      })]
    }), /*#__PURE__*/_jsxs(DialogBody, {
      padding: "0",
      display: "flex",
      children: [renderedStickyFeaturesTable, /*#__PURE__*/_jsxs(Table, {
        textAlign: "center",
        hover: false,
        condensed: true,
        children: [/*#__PURE__*/_jsxs("colgroup", {
          children: [/*#__PURE__*/_jsx("col", {
            style: {
              minWidth: 200
            }
          }), /*#__PURE__*/_jsx("col", {
            span: FLATTENED_TIER_ORDER.length
          })]
        }), /*#__PURE__*/_jsxs("tbody", {
          children: [renderedHubsHeader, renderedHubLevels, renderedFeatureRows, renderedFooter]
        })]
      })]
    })]
  });
}